import { type ReactNode } from 'react'
import { Head, Link } from '@inertiajs/react'
import AdminLayout from '@/components/AdminLayout'
import ResearchNav from '@/components/admin/research/ResearchNav'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { ArrowLeft } from 'lucide-react'

type UsageEvent = {
  id: string
  name: string | null
  event_type: string | null
  date: string | null
  city: string | null
  state: string | null
  status: string | null
}

type UsageOrganization = {
  id: string
  name: string
  events: UsageEvent[]
}

type Props = {
  field_label: string
  value: string
  organizations: UsageOrganization[]
}

function formatDate(dateStr: string | null) {
  if (!dateStr) return '—'
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
}

export default function ResearchReportsUsage({ field_label, value, organizations }: Props) {
  const totalEvents = organizations.reduce((sum, org) => sum + org.events.length, 0)

  return (
    <>
      <Head title={`Research - ${field_label}: ${value}`} />
      <div>
        <h1 className="text-3xl font-semibold tracking-tight font-serif">Research Database</h1>
        <div className="mt-4">
          <ResearchNav />
        </div>

        <Button asChild variant="ghost" size="sm" className="-ml-2 mb-3">
          <Link href="/admin/research/reports">
            <ArrowLeft className="mr-1 size-4" />
            Back to Event Analytics
          </Link>
        </Button>

        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-semibold tracking-tight">
            <span className="text-muted-foreground">{field_label}:</span> {value}
          </h2>
          <Badge variant="secondary">
            {organizations.length} {organizations.length === 1 ? 'organization' : 'organizations'}
          </Badge>
          <Badge variant="secondary">
            {totalEvents} {totalEvents === 1 ? 'event' : 'events'}
          </Badge>
        </div>

        <div className="mt-6 space-y-6">
          {organizations.map(org => (
            <div key={org.id}>
              <div className="mb-2 flex items-baseline gap-2">
                <Link
                  href={`/admin/research/organizations/${org.id}`}
                  className="text-lg font-semibold tracking-tight hover:underline"
                >
                  {org.name}
                </Link>
                <span className="text-sm text-muted-foreground">
                  {org.events.length} {org.events.length === 1 ? 'event' : 'events'}
                </span>
              </div>
              <div className="overflow-hidden rounded-2xl border border-black/10 bg-card shadow-xs">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Event</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {org.events.map(event => (
                      <TableRow key={event.id}>
                        <TableCell className="font-medium">
                          <Link
                            href={`/admin/research/events/${event.id}`}
                            className="hover:underline"
                          >
                            {event.name || 'Untitled'}
                          </Link>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {event.event_type || '—'}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatDate(event.date)}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {[event.city, event.state].filter(Boolean).join(', ') || '—'}
                        </TableCell>
                        <TableCell>
                          {event.status ? <Badge variant="secondary">{event.status}</Badge> : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          ))}
          {organizations.length === 0 && (
            <p className="rounded-2xl border border-black/10 bg-card p-8 text-center text-muted-foreground shadow-xs">
              No organizations found for this value.
            </p>
          )}
        </div>
      </div>
    </>
  )
}

ResearchReportsUsage.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
